import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useData } from '../context/DataContext';

const MegaMenu = ({ variant = 'desktop' }) => {
  const { tree, nav = {} } = useData();
  // Admin can hide the entire mega-menu via Admin → Site Settings → Navigation.
  // Default = visible.
  const showMegaMenu = nav.show_mega_menu !== false;
  const [activeMain, setActiveMain] = useState(null);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const itemRefs = useRef({});
  const containerRef = useRef(null);

  useEffect(() => {
    if (activeMain && itemRefs.current[activeMain] && containerRef.current) {
      const itemRect = itemRefs.current[activeMain].getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      // Position dropdown left edge aligned to the hovered item, clamped to container
      let left = itemRect.left - containerRect.left;
      const dropdownWidth = 760;
      const maxLeft = containerRect.width - dropdownWidth;
      if (left > maxLeft) left = Math.max(0, maxLeft);
      setDropdownLeft(left);
    }
  }, [activeMain]);

  const activeMainData = (tree || []).find((m) => m.id === activeMain);

  if (!tree || tree.length === 0) return null;
  if (!showMegaMenu) return null;

  // Drawer-style (mobile slide-in): show all categories as collapsible accordion-like list
  if (variant === 'drawer') {
    return (
      <div className="bg-white dark:bg-[#0b1220] p-2" data-testid="mega-menu-drawer">
        {tree.map((main) => {
          const isOpen = activeMain === main.id;
          return (
            <div key={main.id} className="border-b border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setActiveMain((p) => (p === main.id ? null : main.id))}
                className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded transition-colors"
                data-testid={`drawer-main-${main.slug}`}
              >
                {main.name}
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="pl-3 pb-2">
                  {(main.categories || []).map((cat) => (
                    <div key={cat.id} className="py-1.5">
                      <Link to={`/category/${cat.slug}`} className="block text-xs font-semibold text-[#2c54b2] dark:text-orange-400 hover:text-[#ee5e23] mb-1">
                        {cat.name}
                      </Link>
                      <ul className="pl-2 space-y-0.5">
                        {(cat.sub_categories || []).slice(0, 8).map((sc) => (
                          <li key={sc.id}>
                            <Link to={`/subcategory/${sc.slug}`} className="text-xs text-gray-600 dark:text-gray-300 hover:text-[#ee5e23] block">
                              {sc.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="bg-white dark:bg-[#0b1220] border-b border-gray-200 dark:border-gray-800 hidden md:block relative"
      onMouseLeave={() => setActiveMain(null)}
      data-testid="mega-menu-desktop"
    >
      <div className="max-w-7xl mx-auto px-4 relative" ref={containerRef}>
        <div className="flex items-center gap-1 text-sm overflow-x-auto">
          {tree.map((main) => (
            <div
              key={main.id}
              ref={(el) => { itemRefs.current[main.id] = el; }}
              onMouseEnter={() => setActiveMain(main.id)}
            >
              <Link
                to={`/main/${main.slug}`}
                className={`px-3 h-11 flex items-center gap-1 whitespace-nowrap border-b-2 transition-colors ${
                  activeMain === main.id
                    ? 'border-[#ee5e23] text-[#ee5e23]'
                    : 'border-transparent text-gray-800 dark:text-gray-200 hover:text-[#ee5e23]'
                }`}
                data-testid={`megamenu-main-${main.slug}`}
              >
                {main.name}
                <ChevronDown className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>

        {/* Dropdown rendered OUTSIDE the overflow-x-auto scroller to prevent vertical clipping */}
        {activeMainData && (activeMainData.categories || []).length > 0 && (
          <div
            className="absolute top-full bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 shadow-xl rounded-md w-[760px] max-w-[calc(100vw-2rem)] grid grid-cols-3 gap-x-6 gap-y-4 p-5 z-50"
            style={{ left: `${dropdownLeft}px` }}
            data-testid={`megamenu-dropdown-${activeMainData.slug}`}
          >
            {(activeMainData.categories || []).map((cat) => (
              <div key={cat.id}>
                <Link
                  to={`/category/${cat.slug}`}
                  className="block text-sm font-semibold text-[#2c54b2] dark:text-orange-400 hover:text-[#ee5e23] mb-1.5 transition-colors"
                >
                  {cat.name}
                </Link>
                <ul className="space-y-1">
                  {(cat.sub_categories || []).slice(0, 6).map((sc) => (
                    <li key={sc.id}>
                      <Link
                        to={`/subcategory/${sc.slug}`}
                        className="text-xs text-gray-600 dark:text-gray-300 hover:text-[#ee5e23] block transition-colors"
                      >
                        {sc.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MegaMenu;
